import { useState, useEffect, useRef } from "react";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import useDark from "./useDark";
import getTheme from "./theme";
import { TILE_CONFIG } from "./constants";
import TileActionWrapper from "./tiles/TileActionWrapper";
import { useCompanyContext } from "../../../context";

const Grid = ({
  tileType,
  currentCase,
  baseMargin = 0,
  onBack,
  hideBack,
  noFixedHeight,
}) => {
  const dark = useDark();
  const t = getTheme(dark);
  const [tiles, setTiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(null);

  const { currentCompany } = useCompanyContext();

  const currentTarget = currentCompany.target;

  useEffect(() => {
    if (noFixedHeight) return;
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerHeight(window.innerHeight - rect.top);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [noFixedHeight]);

  useEffect(() => {
    const config = TILE_CONFIG[tileType];
    if (!config) {
      setTiles([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setTiles([]);
    setIsVisible(false);
    config
      .fetcher(currentTarget)
      .then((response) => {
        const data = response?.data;
        if (!data || !Array.isArray(data)) {
          setTiles([]);
          return;
        }
        setTiles(data);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setIsVisible(true)),
        );
      })
      .catch(() => setTiles([]))
      .finally(() => setIsLoading(false));
  }, [tileType, currentCase]);

  const pad = noFixedHeight ? 0 : Math.max(baseMargin, 24) + 24;
  const heightStyle = noFixedHeight
    ? {}
    : { height: containerHeight || "100%" };

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className="flex flex-col font-poppins font-normal pt-14"
        style={heightStyle}
      >
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (tiles.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex flex-col font-poppins font-normal pt-14"
        style={heightStyle}
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <p className="text-sm text-mutedText">
            No {tileType.toLowerCase()} found
          </p>
        </div>
      </div>
    );
  }

  const config = TILE_CONFIG[tileType];
  const Component = config?.Component;

  const handleTileInteract = (documentId, state) => {
    setTiles((prev) =>
      prev.map((tile) =>
        tile.document_id === documentId ? { ...tile, ...state } : tile,
      ),
    );
  };

  return (
    <div
      ref={containerRef}
      className="font-poppins font-normal"
      style={
        noFixedHeight
          ? { minWidth: 0, overflow: "hidden" }
          : {
              height: containerHeight || "100%",
              overflowY: "auto",
              minWidth: 0,
              overflowX: "hidden",
            }
      }
    >
      <div
        style={{
          paddingBottom: 24,
          paddingTop: noFixedHeight ? 12 : 56,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: config?.singleColumn
              ? "1fr"
              : "repeat(auto-fit, minmax(280px, 1fr))",
            paddingLeft: pad,
            paddingRight: pad,
            minWidth: 0,
          }}
        >
          {tiles
            .filter((item) => config?.canRender?.(item) ?? true)
            .map((item, index) => (
              <div
                key={item.id || item.document_id || index}
                style={{
                  transition: `all 0.4s ease-out ${Math.min(index * 40, 600)}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(14px)",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {config?.noActionWrapper ? (
                  <Component data={item} t={t} />
                ) : (
                  <TileActionWrapper
                    article={item}
                    t={t}
                    onInteract={handleTileInteract}
                  >
                    <Component data={item} t={t} />
                  </TileActionWrapper>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Grid;
